<template>
    <form class="module-time-tracker" ref="form">
        <loading-indicator v-if="loading"/>
        <template v-else>
            <template v-if="request && !revoke">
                <form-row label="Woche wählen" error-name="week_start">
                    <form-input-date v-model="data.week_start" name="week_start" type="week" :min="minDate"/>
                </form-row>

                <form-row error-name="spacer">
                    <div class="infobox infobox-border">
                        <div class="row">
                            <div class="col-lg-12">
                                Für ausgegraute Wochen besteht schon ein Stundenantrag. Hier gelangen Sie zur der Liste der Stundenanträge, um diesen zu bearbeiten: <router-link :to="{name: 'time-tracker-overtime-request-list'}">Link</router-link>
                            </div>
                        </div>
                    </div>
                </form-row>

                <form-row v-if="typeChangeable || change" label="Typ" error-name="type">
                    <form-select v-model="data.type" :options="typeOptions" name="type"/>
                </form-row>
                <form-input v-else v-model="data.type" name="type" type="hidden"/>

                <form-row label="Art" class="default-margin-top" error-name="spacer">
                    <div v-for="option in variantOptions">
                        <label class="inline-flex flex-vertical-middle gap-5 pointer margin-bottom-5">
                            <form-input-radio v-model="variant" :value-on="option.value"/>
                            <span>{{option.label}}</span>
                        </label>
                    </div>
                </form-row>

                <template v-if="variant === variants.chooseDay.value">
                    <form-input :model-value="1" name="dismantling_coretime" type="hidden"/>

                    <template v-if="representationWarning">
                        <template v-if="representationWarning.colliding_regulations && representationWarning.colliding_regulations.length">
                            <template v-for="regulation of representationWarning.colliding_regulations">
                                <h2>{{regulation.title}}</h2>
                                <absence-calendar :year="currentYear" :regulation-id="regulation.id" :ovr-id="id" :display-navigation="false" tag="div"/>
                            </template>
                        </template>
                    </template>

                    <template v-for="(day, dayIndex) of week">
                        <template v-if="workDays.includes(day.key) || data[`${day.key}_absence`]">
                            <div class="flex gap-5 time-tracker-settings-row">
                                <form-row hide-validator-icon label="Ganztägig" :class="[{'hide-label': dayIndex}, 'text-center']">
                                    <div class="input-line-height">
                                        <form-input-checkbox v-model="data[`${day.key}_absence_all_day`]" :name="`${day.key}_absence_all_day`" :disabled="day.date < today"/>
                                        <form-input v-if="day.date < today" :model-value="data[`${day.key}_absence_all_day`] ? 1 : 0" :name="`${day.key}_absence_all_day`" type="hidden"/>
                                    </div>
                                </form-row>

                                <form-row :label="day.name + ', ' + dateHelper('d.m.', day.date)" :label-for="`${day.key}_absence`" :error-name="`${day.key}_absence`"
                                    class="checkbox-label" hide-validator-icon hide-validator>
                                    <div class="flex time-tracker-settings-row">
                                        <form-input :model-value="data[`${day.key}_absence_all_day`] || data[`${day.key}_absence_from`] && data[`${day.key}_absence_to`] ? 1 : 0" :name="`${day.key}_absence`" type="hidden"/>

                                        <form-input-date v-model="data[`${day.key}_absence_from`]" :name="`${day.key}_absence_from`" type="time"
                                            :disabled="data[`${day.key}_absence_all_day`] || day.date < today" :aria-label="'Absesend am ' + day.name + ' von'"/>
                                        <form-input v-if="!data[`${day.key}_absence_all_day`] && day.date < today" :model-value="data[`${day.key}_absence_from`]" :name="`${day.key}_absence_from`" type="hidden"/>

                                        <form-input-date v-model="data[`${day.key}_absence_to`]" :name="`${day.key}_absence_to`" type="time"
                                            :disabled="data[`${day.key}_absence_all_day`] || day.date < today" :aria-label="'Absesend am ' + day.name + ' bis'"/>
                                        <form-input v-if="!data[`${day.key}_absence_all_day`] && day.date < today" :model-value="data[`${day.key}_absence_to`]" :name="`${day.key}_absence_to`" type="hidden"/>
                                    </div>
                                </form-row>

                                <form-row hide-validator-icon label="Stunden" :class="[{'hide-label': dayIndex}, 'text-center']">
                                    <div class="input-line-height">{{formatSeconds(getDayWorkHours(day.key))}}</div>
                                </form-row>
                            </div>
                        </template>
                    </template>

                    <form-row error-name="requested_hours">
                        <form-input v-if="isReduction" :model-value="getDayWorkHoursSum / 3_600" name="requested_hours" type="hidden"/>

                        <div class="time-tracker-overtime-box">
                            Summe: <strong>{{formatSeconds(getDayWorkHoursSum)}}</strong> Stunden
                        </div>

                        <form-error v-if="isReduction" name="absences"/>
                    </form-row>
                </template>
                <template v-else-if="variant === variants.hours.value">
                    <form-input :model-value="0" name="dismantling_coretime" type="hidden"/>
                </template>

                <form-row v-if="variant === variants.hours.value || !isReduction" label="Beantragte Stunden" error-name="requested_hours">
                    <form-input v-model="data.requested_hours" name="requested_hours" placeholder="HH:MM, H.H oder H,H"/>
                    <div v-if="getHours !== ''" class="margin-left-5 margin-top-5">Entspricht {{getHours}}</div>
                    <form-error v-if="!isReduction" name="absences"/>
                </form-row>

                <form-row error-name="spacer">
                    <div class="time-tracker-overtime-box auto-height">
                        <div v-interact="() => $store.dispatch('toggleFold', 'overtimeInfo')">
                            <div :style="{color: remainingOvertimeColor}">
                                Arbeitszeitkonto: {{remainingOvertime}}
                            </div>

                            <div class="item-right">
                                <fold-container group="overtimeInfo" show-open>
                                    <i class="material-icons" aria-hidden="true" role="img" v-tooltip="'Weniger anzeigen'">keyboard_arrow_up</i>
                                </fold-container>

                                <fold-container group="overtimeInfo" show-closed>
                                    <i class="material-icons" aria-hidden="true" role="img"
                                        v-tooltip="'Genehmigte Anträge und verfügbares Arbeitszeitkonto anzeigen'">keyboard_arrow_down</i>
                                </fold-container>
                            </div>
                        </div>

                        <fold-container group="overtimeInfo" show-open>
                            <template v-if="actualAcceptedHoursString && actualAcceptedHoursString !== '00:00'">
                                <hr>

                                <div>
                                    genehmigte Anträge: {{actualAcceptedHoursString}}
                                </div>
                            </template>

                            <template v-if="availableHours">
                                <hr>

                                <div :style="{color: availableHours < 0 ? '#d9534f' : '#26a65b'}">
                                    Arbeitszeitkonto (verfügbar): {{availableHours < 0 ? '-' : '+'}}{{formatSeconds(availableHours * 3600)}}
                                </div>
                            </template>
                        </fold-container>
                    </div>
                </form-row>
            </template>
            <template v-else-if="!request && id">
                <form-row label="Mitarbeiter" error-name="spacer">
                    <div class="time-tracker-reason-box-bold">{{getUsername(data.employee_id, 'employee')}}</div>
                </form-row>
                <form-row label="Begründung" error-name="spacer">
                    <div class="time-tracker-reason-box-bold">{{revoke ? data.revoke_description : data.description}}</div>
                </form-row>
                <div class="row">
                    <div class="col-sm-6">
                        <form-row label="Woche" error-name="spacer">
                            <div class="time-tracker-reason-box-bold">{{weekLabel}}</div>
                        </form-row>
                    </div>
                    <div class="col-sm-6">
                        <form-row label="Beantragt" error-name="spacer">
                            <div class="time-tracker-reason-box-bold">{{data.requested_hours}} Stunden ({{typeLabel}})</div>
                        </form-row>
                    </div>
                </div>
                <template v-if="data.dismantling_coretime">
                    <template v-if="representationWarning.colliding_absences && representationWarning.colliding_absences.length">
                        <div class="info">
                            <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                            <div>
                                An folgenden Tagen liegen Kollisionen mit den Vertretungsreglungen aufgrund abwesender Mitarbeiter vor:
                                <ul>
                                    <li v-for="absence of representationWarning.colliding_absences">{{absence}}</li>
                                </ul>
                            </div>
                        </div>
                    </template>
                    <template v-if="representationWarning.invalid_regulations && representationWarning.invalid_regulations.length">
                        <div class="info">
                            <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                            <div>
                                Die Anzahl benötigter Mitarbeiter gleicht oder übersteigt die der Mitarbeiter in der Vertretungsregelung! Bitte überprüfen Sie die folgenden Vertretungsregelungen:
                                <ul>
                                    <li v-for="regulation of representationWarning.invalid_regulations">{{regulation.title}}</li>
                                </ul>
                            </div>
                        </div>
                    </template>
                    <template v-if="representationWarning.colliding_regulations && representationWarning.colliding_regulations.length">
                        <template v-for="regulation of representationWarning.colliding_regulations">
                            <h2>{{regulation.title}}</h2>
                            <absence-calendar :year="currentYear" :regulation-id="regulation.id" :ovr-id="id" :display-navigation="false" tag="div"/>
                        </template>
                    </template>
                    <form-row label="Abwesenheiten" error-name="spacer">
                        <div class="time-tracker-reason-box-bold">
                            <template v-for="day of week">
                                <template v-if="data[`${day.key}_absence`] === 1">
                                    {{day.name}}: {{data[`${day.key}_absence_all_day`] ? 'Ganztägig' : data[`${day.key}_absence_from`] + ' - ' + data[`${day.key}_absence_to`]}}<br>
                                </template>
                            </template>
                        </div>
                    </form-row>
                </template>
                <template v-if="!revoke">
                    <form-row error-name="spacer">
                        <div class="time-tracker-overtime-box" :style="{color: remainingOvertimeColor}">
                            Arbeitszeitkonto: {{remainingOvertime}}
                        </div>
                    </form-row>

                    <form-row error-name="spacer">
                        <div class="time-tracker-overtime-box" :style="{color: vacationUnusedDaysColor}">
                            Resturlaub: {{vacationUnusedDays}} Tag{{1 !== vacationUnusedDays ? 'e' : ''}}
                        </div>
                    </form-row>

                    <form-row label="Erstellt" error-name="spacer">
                        <div class="time-tracker-reason-box-bold">{{data.created ? dateHelper('d.m.Y H:i:s', data.created) : '-'}}</div>
                    </form-row>
                </template>
            </template>
            <template v-else-if="representationWarning">
                <template v-if="representationWarning.colliding_absences && representationWarning.colliding_absences.length">
                    <div class="info">
                        <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                        <div>
                            An folgenden Tagen liegen Kollisionen mit den Vertretungsreglungen aufgrund abwesender Mitarbeiter vor:
                            <ul>
                                <li v-for="absence of representationWarning.colliding_absences">{{absence}}</li>
                            </ul>
                        </div>
                    </div>
                </template>

                <template v-if="representationWarning.invalid_regulations && representationWarning.invalid_regulations.length">
                    <div class="info">
                        <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                        <div>
                            Die Anzahl benötigter Mitarbeiter gleicht oder übersteigt die der Mitarbeiter in der Vertretungsregelung! Bitte überprüfen Sie die folgenden Vertretungsregelungen:
                            <ul>
                                <li v-for="regulation of representationWarning.invalid_regulations">{{regulation.title}}</li>
                            </ul>
                        </div>
                    </div>
                </template>
            </template>

            <form-row label="Begründung" :error-name="inputFormName">
                <form-textarea v-model="data[inputFormName]" :name="inputFormName" class="textarea-vertical"/>
            </form-row>
        </template>
    </form>
</template>

<script lang="ts">
    import FoldContainer from '@/components/FoldContainer.vue';
    import FormError from '@/components/form/FormError.vue';
    import FormInputRadio from '@/components/form/FormInputRadio.vue';
    import {getUsername} from '@/filters/username';
    import {dayNames, dayNamesEn, incrementDate, setToMonday, weekDays} from '@/library/date';
    import {formSubmit, getFormData, parseFormData} from '@/library/form';
    import {toInt} from '@/library/math';
    import {
        OVERTIME_REVOKE_STATE_ACCEPTED,
        OVERTIME_REVOKE_STATE_DECLINED,
        OVERTIME_REVOKE_STATE_WAIT,
        OVERTIME_STATE_ACCEPT,
        OVERTIME_STATE_REJECT,
    } from '@/library/modules/time-tracker/overtime';
    import {getSecondsByTimeString, POPUP_ACTION_MODE_ACCEPT, POPUP_ACTION_MODE_BOTH, POPUP_ACTION_MODE_DECLINE} from '@/library/modules/time-tracker/time-tracker';
    import {popupDefaultCancelButton, popupDefaultCloseButton} from '@/library/popup';
    import {errorHandler} from '@/utils/portuna';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import FormInput from '../../../components/form/FormInput.vue';
    import FormInputCheckbox from '../../../components/form/FormInputCheckbox.vue';
    import FormInputDate from '../../../components/form/FormInputDate.vue';
    import FormRow from '../../../components/form/FormRow.vue';
    import FormSelect from '../../../components/form/FormSelect.vue';
    import FormTextarea from '../../../components/form/FormTextarea.vue';
    import LoadingIndicator from '../../../components/LoadingIndicator.vue';
    import formatSeconds from '../../../filters/formatSeconds';
    import TopMessageHandler from '../../../library/topMessageHandler';
    import dateHelper from '../../../utils/portuna/dateHelper';
    import AbsenceCalendar from '../../absences/AbsenceCalendar.vue';

    @Component({
        name: 'OvertimePopup',
        components: {
            FormError,
            FormInputRadio,
            FoldContainer,
            AbsenceCalendar,
            LoadingIndicator,
            FormSelect,
            FormInputCheckbox,
            FormTextarea,
            FormInput,
            FormInputDate,
            FormRow
        },
        methods: {
            toInt,
            formatSeconds,
            dateHelper,
            getUsername
        },
        emits: ['setButtons', 'update', 'add'],
    })
    export default class OvertimePopup extends Vue
    {
        readonly $refs: {
            form: HTMLFormElement,
        };

        @Prop({
            type: Number,
            default: 0,
            required: false,
        }) id: number;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) request: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) change: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) revoke: boolean;

        @Prop({
            type: Number,
            default: null,
            required: false,
        }) type: Nullable<0|1>;

        @Prop({
            type: Number,
            default: null,
            required: false,
        }) actionType: Nullable<number>;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) typeChangeable: boolean;

        currentYear = dateHelper('Y') as number;

        today = dateHelper('Y-m-d') as string;

        workDays: string[] = [];

        remainingOvertime: string = '';

        remainingOvertimeColor: string = '';

        representationWarning: VacationRegulationWarning = {};

        minDate: Nullable<string> = null;

        min: Nullable<number> = null;

        max: Nullable<number> = null;

        vacationUnusedDays: number = 0;

        actualAcceptedHoursString: string = '';

        availableHours: number = 0;

        hasCoreTimeSettings: boolean = false;

        weekWorkHours: Record<string, number> = {};

        data = {
            employee_id: 0,
            week_start: dateHelper('Y-m-d', setToMonday(new Date())),
            type: null,
            requested_hours: 0,
            dismantling_coretime: 0,
            monday_absence: 0,
            monday_absence_from: null,
            monday_absence_to: null,
            monday_absence_all_day: 0,
            tuesday_absence: 0,
            tuesday_absence_from: null,
            tuesday_absence_to: null,
            tuesday_absence_all_day: 0,
            wednesday_absence: 0,
            wednesday_absence_from: null,
            wednesday_absence_to: null,
            wednesday_absence_all_day: 0,
            thursday_absence: 0,
            thursday_absence_from: null,
            thursday_absence_to: null,
            thursday_absence_all_day: 0,
            friday_absence: 0,
            friday_absence_from: null,
            friday_absence_to: null,
            friday_absence_all_day: 0,
            saturday_absence: 0,
            saturday_absence_from: null,
            saturday_absence_to: null,
            saturday_absence_all_day: 0,
            sunday_absence: 0,
            sunday_absence_from: null,
            sunday_absence_to: null,
            sunday_absence_all_day: 0,
            description: '',
            revoke_description: '',
            message: '',
            created: null,
        };

        previousData: typeof this.data = {};

        variant: number = this.variantOptions[0].value;

        loading: boolean = true;

        get isReduction()
        {
            return this.data.type === 1;
        }

        get variants()
        {
            let labelAddition = this.hasCoreTimeSettings ? '(außerhalb der Kernzeit) ' : '';

            return {
                chooseDay: {
                    label: this.isReduction ? 'Ganzen Arbeitstag oder einzelne Stunden abbauen' : 'Zusätzliche Abwesenheit hinterlegen',
                    value: 0,
                },
                hours: {
                    label: this.isReduction ? `Stunden in der Woche ${labelAddition}abbauen` : 'Nur Überstunden beantragen',
                    value: 1,
                },
            };
        }

        get variantOptions(): SelectOption[]
        {
            let variants = Object.values(this.variants);

            return this.isReduction ? variants : variants.reverse();
        }

        @Watch('data', {deep: true})
        watchData()
        {
            // check regulations
            this.representationRegulationCheck();

            for (const day of weekDays)
            {
                if (
                    !this.data[`${day.key}_absence_all_day`] && this.previousData[`${day.key}_absence_all_day`]
                    && this.data[`${day.key}_absence_from`] === '00:00' && this.data[`${day.key}_absence_to`] === '23:59'
                )
                {
                    this.data[`${day.key}_absence_from`] = null;
                    this.data[`${day.key}_absence_to`] = null;
                }
            }

            console.log(this.data);

            this.previousData = Object.assign({}, this.data);
        }

        @Watch('data.type')
        watchType()
        {
            for (let validator of ['Between', 'GreaterThan'])
            {
                this.$store.dispatch(
                    'form/removeValidator',
                    {
                        element: 'requested_hours',
                        name: validator,
                    },
                );
            }

            if (0 === this.data.type)
            {
                if(!this.id)
                {
                    this.data.dismantling_coretime = 0;
                }

                if (null === this.max)
                {
                    // overtime
                    this.$store.dispatch('form/addValidator', {
                        element: 'requested_hours',
                        validator: {
                            'name': 'GreaterThan',
                            'options': {
                                'min': 0,
                                'inclusive': true,
                            },
                        },
                    });
                }
                else
                {
                    // overtime
                    this.$store.dispatch('form/addValidator', {
                        element: 'requested_hours',
                        validator: {
                            'name': 'Between',
                            'options': {
                                'min': 0,
                                'max': this.max,
                                'inclusive': true,
                                'messages': {
                                    notBetween: 'Sie dürfen entsprechend Ihrer Stundenkonto-Einstellungen maximal "%max%" Stunden beantragen. Ihr Eingabewert muss daher zwischen "%min%" und "%max%", diese Werte mit eingeschlossen, liegen.',
                                }
                            },
                        },
                    });
                }
            }
            else if(1 === this.data.type && null !== this.min)
            {
                if(!this.id)
                {
                    this.data.dismantling_coretime = 1;
                }

                // overtime cut
                this.$store.dispatch('form/addValidator', {
                    element: 'requested_hours',
                    validator: {
                        'name': 'Between',
                        'options': {
                            'min': 0,
                            'max': this.min,
                            'inclusive': true,
                            'messages': {
                                notBetween: 'Sie dürfen entsprechend Ihrer Stundenkonto-Einstellungen maximal \'%max%\' Stunden beantragen. Ihr Eingabewert muss daher zwischen \'%min%\' und \'%max%\', diese Werte mit eingeschlossen, liegen.',
                            }
                        },
                    },
                });
            }

            this.variant = this.isReduction ? this.variants.chooseDay.value : this.variants.hours.value;
        }

        get buttons()
        {
            let buttons: PopupButton[] = [popupDefaultCancelButton];

            if(this.revoke)
            {
                buttons.push({
                    type: 'primary',
                    title: 'Zurückziehen',
                    icon: 'undo',
                    cmd: () => this.post(null, OVERTIME_REVOKE_STATE_WAIT),
                });
            }
            else
            {
                buttons.push({
                    type: 'primary',
                    title: 'Beantragen',
                    icon: 'assignment',
                    cmd: () => this.post(),
                });
            }

            return buttons;
        }

        get superiorButtons()
        {
            let buttons: PopupButton[] = [popupDefaultCloseButton];

            if (POPUP_ACTION_MODE_ACCEPT === this.actionType)
            {
                const params = this.revoke ? [null, OVERTIME_REVOKE_STATE_ACCEPTED] : [OVERTIME_STATE_ACCEPT];
                buttons.push({
                    type: 'success',
                    title: 'Annehmen',
                    icon: 'check',
                    cmd: () => this.post(...params),
                });
            }
            else if (POPUP_ACTION_MODE_DECLINE === this.actionType)
            {
                const params = this.revoke ? [null, OVERTIME_REVOKE_STATE_DECLINED] : [OVERTIME_STATE_REJECT];
                buttons.push({
                    type: 'danger',
                    title: 'Ablehnen',
                    icon: 'clear',
                    cmd: () => this.post(...params),
                });
            }
            else if (POPUP_ACTION_MODE_BOTH === this.actionType)
            {
                const successParams = this.revoke ? [null, OVERTIME_REVOKE_STATE_ACCEPTED] : [OVERTIME_STATE_ACCEPT];
                const rejectParams  = this.revoke ? [null, OVERTIME_REVOKE_STATE_DECLINED] : [OVERTIME_STATE_REJECT];
                buttons.push(
                    {
                        type: 'success',
                        title: 'Annehmen',
                        icon: 'check',
                        cmd: () => this.post(...successParams),
                    },
                    {
                        type: 'danger',
                        title: 'Ablehnen',
                        icon: 'clear',
                        cmd: () => this.post(...rejectParams),
                    },
                );
            }

            return buttons;
        }

        get typeLabel()
        {
            return this.typeOptions.reduce((context, option) => ({...context, [option.value]: option.label}), {})[this.data.type as number];
        }

        get typeOptions(): SelectOption[]
        {
            return [
                {
                    label: 'Überstunden',
                    value: 0,
                },
                {
                    label: 'Stundenabbau',
                    value: 1,
                },
            ];
        }

        get week()
        {
            const week: typeof weekDays[keyof weekDays] & { date: string }[] = [];

            const weekStart = dateHelper('Y-m-d', this.data.week_start as string);

            for (let index of Object.keys(weekDays))
            {
                if (!weekDays.hasOwnProperty(index))
                {
                    continue;
                }

                week[index] = Object.assign(weekDays[index], {date: dateHelper('Y-m-d', incrementDate(new Date(weekStart as string), parseInt(index)))});
            }

            return week.filter((day) => day.date >= this.today || this.data[`${day.key}_absence`]);
        }

        get weekLabel()
        {
            const monday = dateHelper('d.m.Y', this.data.week_start as string);
            const sunday = dateHelper('d.m.Y', incrementDate(new Date(this.data.week_start as string), 6));

            return `${monday} - ${sunday}`;
        }

        get vacationUnusedDaysColor()
        {
            if (this.vacationUnusedDays < 0)
            {
                return '#b4171b';
            }

            if (this.vacationUnusedDays > 0)
            {
                return '#26a65b';
            }

            return '';
        }

        get inputFormName()
        {
            if (this.request)
            {
                return this.revoke ? 'revoke_description' : 'description';
            }

            return 'message';
        }

        get getHours()
        {
            return this.formatHours('' + this.data.requested_hours);
        }

        get getDayWorkHoursSum()
        {
            let workHoursSum = 0;

            for (const day of this.week)
            {
                if (!this.workDays.includes(day.key))
                {
                    continue;
                }

                workHoursSum += this.getDayWorkHours(day.key);
            }

            return workHoursSum;
        }

        created()
        {
            this.fetch();
            this.emitter.emit('setButtons', []);
            this.$emit('setButtons', []);

            if (this.type !== null)
            {
                this.$emit('setTitle', this.type);
            }

            this.$store.dispatch('setFold', {group: 'overtimeInfo', openState: false});
        }

        fetch()
        {
            this.axios.get(this.getUrl())
            .then(({data: {data: r}}) =>
            {
                this.$store.dispatch('form/setFilter', r.filter);

                this.workDays                  = r.workDays;
                this.remainingOvertime         = r.remainingOvertime;
                this.remainingOvertimeColor    = r.remainingOvertimeColor;
                this.representationWarning     = r.representationWarning;
                this.max                       = r.max;
                this.min                       = r.min;
                this.vacationUnusedDays        = r.vacation_unused_days;
                this.actualAcceptedHoursString = r.actualAcceptedHoursString;
                this.availableHours            = r.availableHours;
                this.hasCoreTimeSettings       = r.hasCoreTimeSettings;
                this.weekWorkHours             = r.weekWorkHours;

                if (this.request)
                {
                    this.minDate = r.minDate;
                }

                if (this.id)
                {
                    for (let key in this.data)
                    {
                        if (this.data.hasOwnProperty(key) && r.data.hasOwnProperty(key))
                        {
                            this.data[key] = r.data[key];
                        }
                    }

                    if (this.request && (this.change || this.revoke))
                    {
                        this.emitter.emit('setButtons', this.buttons);
                        this.$emit('setButtons', this.buttons);
                        this.data.description        = '';
                        this.data.revoke_description = '';
                    }
                    else
                    {
                        this.emitter.emit('setButtons', this.superiorButtons);
                        this.$emit('setButtons', this.superiorButtons);
                        this.data.message = '';
                    }
                }
                else
                {
                    this.emitter.emit('setButtons', this.buttons);
                    this.$emit('setButtons', this.buttons);
                }

                if (null === this.data.type || this.type !== null)
                {
                    this.data.type = this.type;
                }

                this.loading = false;
                this.emitter.emit('lazy-load-focus');

                // check regulations
                this.representationRegulationCheck();

                // handle types
                this.watchType();
            })
            .catch((err) =>
            {
                errorHandler(err);
                this.emitter.emit('close');
            });
        }

        formatHours(hours: string)
        {
            let splitTime = [];

            if (hours.indexOf(',') === -1 && hours.indexOf('.') === -1 && hours.indexOf(':') === -1)
            {
                return `${hours}h 00min`;
            }

            if (hours.indexOf(',') !== -1)
            {
                splitTime = hours.split(',');
            }
            else if (hours.indexOf('.') !== -1)
            {
                splitTime = hours.split('.');
            }
            else if (hours.indexOf(':') !== -1)
            {
                splitTime = hours.split(':');
                return `${splitTime[0]}h ${splitTime[1]}min`;
            }
            else
            {
                return '';
            }

            if (splitTime.length === 2)
            {
                splitTime[1] = '0.' + splitTime[1];

                let minutes = Math.round(parseFloat(splitTime[1]) * 60);

                return `${splitTime[0]}h ${(minutes < 10 ? '0' : '') + minutes}min`;
            }

            return '';
        }

        formatRequestedHours(formData: Record<string, string | number>)
        {
            let hours = `${formData.requested_hours}`;
            if (hours.indexOf(':') === -1)
            {
                formData.requested_hours = this.formatHours(hours).replace('h ', ':').replace('min', '');
            }
        }

        post(status: Nullable<number> = null, revokeStatus: Nullable<number> = null)
        {
            let url = this.getUrl(true);

            if(null !== status)
            {
                url += `/status/${status}`;
            }

            if(null !== revokeStatus)
            {
                url += `/revoke-status/${revokeStatus}`;
            }

            let formData: Record<string, string|number> = getFormData(this.$refs.form);

            if (formData.requested_hours)
            {
                this.formatRequestedHours(formData);
            }

            formSubmit(url, parseFormData(formData))
            .then(data => {
                if (this.change)
                {
                    if(this.request)
                    {
                        TopMessageHandler.success(`Sie haben erfolgreich eine Änderung für den Stundenantrag beantragt.`);
                    } else {
                        TopMessageHandler.success(`Sie haben den Änderungsantrag erfolgreich ${1 === status ? 'angenommen' : 'abgelehnt'}.`);
                    }
                    this.emitter.emit('emit', {event: 'update', data: data.entry})
                    this.$emit('update', data.entry);
                }
                else if (this.revoke)
                {
                    if(1 === revokeStatus)
                    {
                        TopMessageHandler.success(`Sie haben erfolgreich eine Zurückziehung beantragt.`)
                    }
                    else
                    {
                        TopMessageHandler.success(`Der Antrag auf Zurückziehung wurde erfolgreich ${2 === revokeStatus ? 'angenommen' : 'abgelehnt'}.`)
                    }

                    this.emitter.emit('emit', {event: 'update', data});
                    this.$emit('update', data);
                }
                else if (this.id)
                {
                    TopMessageHandler.success(`Sie haben den Stundenantrag erfolgreich ${1 === status ? 'angenommen' : 'abgelehnt'}.`);
                    this.emitter.emit('emit', {event: 'update', data: {state: status, editor: this.currentUser.id, edited: dateHelper('Y-m-d H:i:s', new Date())}})
                    this.$emit(
                        'update',
                        {
                            state: status,
                            editor: this.currentUser.id,
                            edited: dateHelper('Y-m-d H:i:s', new Date()),
                        },
                    );
                } else {
                    TopMessageHandler.success(`Sie haben erfolgreich ${'1' === formData.type ? 'Stundenabbau' : 'Überstunden'} beantragt!`);
                    this.emitter.emit('emit', {event: 'add', data: data.entries})
                    this.$emit('add', data.entries);
                }

                this.emitter.emit('close');
            })
            .catch(console.error);
        }

        getUrl(isPost: boolean = false)
        {
            let action = 'request';
            let params = '';

            if (this.change)
            {
                action = this.request ? 'request-request-change' : 'change-change-status';
            }
            else if (this.id)
            {
                action = isPost ? 'change-request-status' : 'edit-request';
                params += (this.request ? '/request/1' : '') + (this.revoke ? '/revoke/1' : '');
            }

            if (this.id)
            {
                params += `/id/${this.id}`;
            }

            return `/employee/time-tracker/overtime/${action}` + params;
        }

        representationRegulationCheck()
        {
            if(this.data.dismantling_coretime && this.data.week_start)
            {
                let formData = this.data;

                this.axios.post('/employee/time-tracker/overtime/request-regulation-check', parseFormData(formData))
                .then(({data: {data}}) => {
                    this.representationWarning = data;
                });
            }
        }

        getDayName(day: string)
        {
            return dayNames[dayNamesEn.indexOf(day.charAt(0).toUpperCase() + day.slice(1))];
        }

        getDayWorkHours(dayKey: string): number
        {
            if (this.data[`${dayKey}_absence_all_day`])
            {
                return this.weekWorkHours[dayKey];
            }

            if (!this.data[`${dayKey}_absence_from`] || !this.data[`${dayKey}_absence_to`])
            {
                return 0;
            }

            return getSecondsByTimeString(this.data[`${dayKey}_absence_to`]) - getSecondsByTimeString(this.data[`${dayKey}_absence_from`]);
        }
    }
</script>